import React, { useState, useEffect } from "react";
import { useLanguage } from '../context/LanguageContext';
import { useDialogs } from '../context/DialogContext';
import adminApi from '../adminApi';
import { backendUrl } from "../../backendUrl";
import "./SystemOptionsMapping.css";

interface LockProperty {
  id: number;
  name: string;
  column_name: string | null;
  icon_path: string;
  display_order: number;
  is_active: boolean;
}

// Wiersz optionstable - klucz to optionid (tabela z importu) albo id (schemat z sqlite.js),
// cechy systemu są w kolumnach z wartościami "Ja"/"Nein"
interface SystemOption {
  optionid?: number;
  id?: number;
  supllier: string;
  system1: string;
  slogan: string;
  [column: string]: string | number | null | undefined;
}

// Stan przełączników według kolumny w optionstable
interface ColumnConfig {
  [columnName: string]: boolean;
}

// Konfigurator pokazuje maksymalnie 8 aktywnych właściwości (getSystemProperties)
const MAX_SHOWN_PROPERTIES = 8;

const systemKey = (system: SystemOption) => (system.optionid ?? system.id) as number;

const isYes = (value: unknown) => String(value ?? "").trim().toLowerCase() === "ja";

function SystemOptionsMapping() {
  const { t } = useLanguage();
  const { notify } = useDialogs();
  const [properties, setProperties] = useState<LockProperty[]>([]);
  const [systems, setSystems] = useState<SystemOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSystem, setSelectedSystem] = useState<SystemOption | null>(null);
  const [config, setConfig] = useState<ColumnConfig>({});
  const [saving, setSaving] = useState(false);

  // Dane raz, przy wejściu do widoku (fetchData to zwykła funkcja komponentu)
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async (keepSelectedId?: number) => {
    try {
      setLoading(true);
      const [propsRes, systemsRes] = await Promise.all([
        adminApi.get('/lock-properties'),
        adminApi.get('/system-options')
      ]);

      const activeProps = propsRes.data
        .filter((p: LockProperty) => p.is_active)
        .sort((a: LockProperty, b: LockProperty) => a.display_order - b.display_order)
        .slice(0, MAX_SHOWN_PROPERTIES);
      setProperties(activeProps);
      setSystems(systemsRes.data);

      if (keepSelectedId !== undefined) {
        const updated = systemsRes.data.find((s: SystemOption) => systemKey(s) === keepSelectedId);
        if (updated) selectSystem(updated, activeProps);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectSystem = (system: SystemOption, props: LockProperty[] = properties) => {
    setSelectedSystem(system);
    const columnConfig: ColumnConfig = {};
    props.forEach((prop) => {
      if (prop.column_name) columnConfig[prop.column_name] = isYes(system[prop.column_name]);
    });
    setConfig(columnConfig);
  };

  // Kilka właściwości może wskazywać tę samą kolumnę - przełączają się razem
  const handleToggle = (columnName: string) => {
    setConfig((prev) => ({ ...prev, [columnName]: !prev[columnName] }));
  };

  const handleSave = async () => {
    if (!selectedSystem) return;

    setSaving(true);
    try {
      await adminApi.put(`/system-options/${systemKey(selectedSystem)}`, { properties: config });
      notify(t("Configuration saved successfully!"), "success");
      await fetchData(systemKey(selectedSystem));
    } catch (error) {
      console.error("Error saving configuration:", error);
      notify(t("Failed to save configuration"), "error");
    } finally {
      setSaving(false);
    }
  };

  const sharedColumns = new Set(
    properties
      .map((p) => p.column_name)
      .filter((c, i, all): c is string => !!c && all.indexOf(c) !== i)
  );

  if (loading && systems.length === 0) return <div className="loading">{t("Loading...")}</div>;

  return (
    <div className="system-options-mapping">
      <h2>{t("System Options Mapping")}</h2>
      <p className="subtitle">{t("Configure which lock properties are enabled for each system")}</p>

      <div className="mapping-container">
        {/* Lista systemów */}
        <div className="systems-list">
          <h3>{t("Systems")}</h3>
          <div className="systems-scroll">
            {systems.map(system => (
              <div
                key={systemKey(system)}
                className={`system-item ${selectedSystem && systemKey(selectedSystem) === systemKey(system) ? 'selected' : ''}`}
                onClick={() => selectSystem(system)}
              >
                <div className="system-name">{system.supllier}</div>
                <div className="system-code">{system.system1}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Konfiguracja opcji */}
        <div className="options-config">
          {selectedSystem ? (
            <>
              <div className="config-header">
                <div>
                  <h3>{selectedSystem.supllier} - {selectedSystem.system1}</h3>
                  <p className="system-slogan">{selectedSystem.slogan}</p>
                </div>
                <button className="btn-save" onClick={handleSave} disabled={saving}>
                  {t("Save Configuration")}
                </button>
              </div>

              {sharedColumns.size > 0 && (
                <p className="shared-column-hint">
                  {t("Some properties use the same column and are switched together:")} {Array.from(sharedColumns).join(", ")}
                </p>
              )}

              <div className="options-grid">
                {properties.map(property => {
                  const column = property.column_name;
                  const enabled = column ? !!config[column] : false;
                  return (
                    <div key={property.id} className={`option-card ${column ? '' : 'option-card--unmapped'}`}>
                      <div className="option-icon">
                        <img
                          src={`${backendUrl}${property.icon_path}`}
                          alt={property.name}
                          style={{
                            width: "32px",
                            height: "32px",
                            filter: enabled ? "none" : "grayscale(100%) opacity(0.4)"
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </div>
                      <div className="option-info">
                        <div className="option-text">
                          <div className="option-name">{property.name}</div>
                          <div className={`option-column ${column && sharedColumns.has(column) ? 'option-column--shared' : ''}`}>
                            {column ? `${t("Column")}: ${column}` : t("No column assigned")}
                          </div>
                        </div>
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={enabled}
                            disabled={!column}
                            onChange={() => column && handleToggle(column)}
                          />
                          <span className="slider"></span>
                        </label>
                        <span className="option-status">
                          {enabled ? t("Enabled") : t("Disabled")}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="no-selection">
              <p>{t("Select a system from the list to configure its options")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SystemOptionsMapping;
